import { Component, OnInit, TemplateRef, ViewChild, NgZone } from '@angular/core';
import { ApiService } from '../../shared/services/api.service';
import { ColDef } from 'ag-grid-community';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule } from '@angular/forms';

@Component({
  standalone: false,
  selector: 'app-customer-limits',
  templateUrl: './customer-limits.component.html',
  styleUrl: './customer-limits.component.scss'
})
export class CustomerLimitsComponent implements OnInit {
  customerLimits: any[] = [];
  loading = false;
  page = 1;
  limit = 500; // Get all records for AG Grid
  total = 0;
  gridApi: any;
  selectedLimit: any = null;
  editForm: any = {
    dailyLimit: 0,
    monthlyLimit: 0,
    dailyTransactionLimit: 0,
    monthlyTransactionLimit: 0
  };
  
  @ViewChild('editModal', { static: true }) editModal!: TemplateRef<any>;
  modalRef: any;
  
  @ViewChild('actionTemplate', { static: true }) actionTemplate!: TemplateRef<any>;

  columnDefs: ColDef[] = [
    {
      headerName: 'Action',
      field: 'action',
      cellRenderer: (params) => {
        const button = document.createElement('button');
        button.classList.add('btn', 'btn-sm', 'btn-primary', 'active', 'rounded-pill');
        button.innerHTML = '<i class="bi bi-eye-fill"></i>';
        button.addEventListener('click', () => {
          this.zone.run(() => {
            this.openEditModal(params.data);
          });
        });
        return button;
      },
      width: 80,
      sortable: false,
      filter: false,
    },
    {
      headerName: 'Customer Name',
      field: 'customerId.name',
      sortable: true,
      filter: true,
      width: 150,
      valueGetter: (params) => params.data.customerId?.name || params.data.customerId?.emailId || 'N/A'
    },
    {
      headerName: 'Customer Email',
      field: 'customerId.emailId',
      sortable: true,
      filter: true,
      width: 200
    },
    {
      headerName: 'Country',
      field: 'countryId.displayName',
      sortable: true,
      filter: true,
      width: 150
    },
    {
      headerName: 'Daily Limit',
      field: 'dailyLimit',
      sortable: true,
      filter: true,
      width: 120,
      valueFormatter: (params) => params.value?.toLocaleString() || '0'
    },
    {
      headerName: 'Monthly Limit',
      field: 'monthlyLimit',
      sortable: true,
      filter: true,
      width: 120,
      valueFormatter: (params) => params.value?.toLocaleString() || '0'
    },
    {
      headerName: 'Daily Tx Limit',
      field: 'dailyTransactionLimit',
      sortable: true,
      filter: true,
      width: 130,
      valueFormatter: (params) => params.value?.toLocaleString() || '0'
    },
    {
      headerName: 'Monthly Tx Limit',
      field: 'monthlyTransactionLimit',
      sortable: true,
      filter: true,
      width: 140,
      valueFormatter: (params) => params.value?.toLocaleString() || '0'
    },
    {
      headerName: 'Daily Usage',
      field: 'dailyUsage',
      sortable: true,
      filter: true,
      width: 150,
      valueGetter: (params) => `${params.data.dailyUsage?.amount?.toLocaleString() || 0} (${params.data.dailyUsage?.count || 0} txns)`,
    },
    {
      headerName: 'Monthly Usage',
      field: 'monthlyUsage',
      sortable: true,
      filter: true,
      width: 150,
      valueGetter: (params) => `${params.data.monthlyUsage?.amount?.toLocaleString() || 0} (${params.data.monthlyUsage?.count || 0} txns)`,
    },
    {
      headerName: 'Daily Last Reset',
      field: 'dailyUsage.lastReset',
      sortable: true,
      filter: true,
      width: 150,
      valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString() : 'N/A'
    },
    {
      headerName: 'Monthly Last Reset',
      field: 'monthlyUsage.lastReset',
      sortable: true,
      filter: true,
      width: 150,
      valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString() : 'N/A'
    },
    
  ];

  gridOptions: any = {
    columnDefs: this.columnDefs,
    rowSelection: 'multiple',
    pagination: true,
    paginationPageSize: 20,
    domLayout: 'normal',
    defaultColDef: {
      editable: false,
      sortable: true,
      filter: true,
    },
    onGridReady: (params: any) => {
      this.gridApi = params.api;
      params.api.sizeColumnsToFit();
      window.addEventListener('resize', () => {
        setTimeout(() => params.api.sizeColumnsToFit());
      });
    }
  };

  constructor(
    private _apiService: ApiService,
    private modalService: NgbModal,
    private zone: NgZone
  ) { }

  ngOnInit() {
    this.loadCustomerLimits();
  }

  loadCustomerLimits() {
    this.loading = true;
    // Get all records for AG Grid
    const query = `?page=0&limit=${this.limit}`;
    
    this._apiService.getData('/admin/customer-limits' + query).subscribe({
      next: (res: any) => {
        if (res && !res.error) {
          this.customerLimits = res.data.customerLimits || [];
          this.total = res.data.total || 0;
          if (this.gridApi) {
            this.gridApi.setGridOption('rowData', this.customerLimits);
          }
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  deleteLimit(id: string) {
    if (confirm('Are you sure you want to delete this customer limit?')) {
      this._apiService.deleteData(`/admin/customer-limits/${id}`).subscribe({
        next: (res: any) => {
          if (res && !res.error) {
            this.loadCustomerLimits();
            alert('Customer limit deleted successfully!');
          } else {
            alert(res.message || 'Error deleting customer limit');
          }
        },
        error: (err: any) => {
          alert('Error deleting customer limit: ' + err.message);
        }
      });
    }
  }

  resetDailyUsage(limitId: string) {
    this._apiService.postData(`/admin/customer-limits/${limitId}/reset-daily`, {}).subscribe({
      next: (res: any) => {
        if (res && !res.error) {
          this.loadCustomerLimits();
          alert('Daily usage reset successfully!');
        } else {
          alert(res.message || 'Error resetting daily usage');
        }
      },
      error: (err: any) => {
        alert('Error resetting daily usage: ' + err.message);
      }
    });
  }

  resetMonthlyUsage(limitId: string) {
    this._apiService.postData(`/admin/customer-limits/${limitId}/reset-monthly`, {}).subscribe({
      next: (res: any) => {
        if (res && !res.error) {
          this.loadCustomerLimits();
          alert('Monthly usage reset successfully!');
        } else {
          alert(res.message || 'Error resetting monthly usage');
        }
      },
      error: (err: any) => {
        alert('Error resetting monthly usage: ' + err.message);
      }
    });
  }

  onGridReady(params: any) {
    this.gridApi = params.api;
    params.api.sizeColumnsToFit();
    window.addEventListener('resize', () => {
      setTimeout(() => params.api.sizeColumnsToFit());
    });
  }
  
  openEditModal(limit: any) {
    this.selectedLimit = { ...limit }; // Create a copy to avoid direct reference
    this.editForm = {
      dailyLimit: limit.dailyLimit,
      monthlyLimit: limit.monthlyLimit,
      dailyTransactionLimit: limit.dailyTransactionLimit,
      monthlyTransactionLimit: limit.monthlyTransactionLimit
    };
    this.modalRef = this.modalService.open(this.editModal, { backdrop: 'static', size: 'lg' });
  }
  
  closeModal() {
    if (this.modalRef) {
      this.modalRef.close();
    }
  }
  
  isFormValid(): boolean {
    return this.editForm.dailyLimit >= 0 && 
           this.editForm.monthlyLimit >= 0 && 
           this.editForm.dailyTransactionLimit >= 0 && 
           this.editForm.monthlyTransactionLimit >= 0;
  }
  
  updateLimit() {
    if (!this.isFormValid()) {
      alert('Please enter valid values for all fields');
      return;
    }
    
    if (!this.selectedLimit) {
      alert('No limit selected for update');
      return;
    }
    
    const updateData = {
      dailyLimit: this.editForm.dailyLimit,
      monthlyLimit: this.editForm.monthlyLimit,
      dailyTransactionLimit: this.editForm.dailyTransactionLimit,
      monthlyTransactionLimit: this.editForm.monthlyTransactionLimit
    };
    
    this._apiService.putData(`/admin/customer-limits/${this.selectedLimit._id}`, updateData).subscribe({
      next: (res: any) => {
        if (res && !res.error) {
          this.loadCustomerLimits();
          this.closeModal();
        } else {
          alert(res.message || 'Error updating customer limit');
        }
      },
      error: (err: any) => {
        alert('Error updating customer limit: ' + err.message);
      }
    });
  }
}